import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { db, SYNC_STATUS, recordHistoryBatch } from '../db/shiksha-db';
import { RegisterGrid } from '../components/RegisterGrid';
import { ArrowLeft, Download, UploadCloud, ShieldAlert } from 'lucide-react';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { useAuth } from '../hooks/useAuth';
import { MARKING_SCHEMES } from '../config/marking-schemes';

/**
 * Resolve a classId URL param to a MARKING_SCHEMES key.
 * Handles short IDs like 'c9', 'c10', 'c6' and full names like 'Class IX'.
 */
function resolveClassKey(classId) {
  if (!classId) return null;
  const upper = classId.toUpperCase();
  const parts = upper.split('-');
  const baseClass = parts[0].trim();

  // Short ID mapping
  const shortMap = {
    'C6': 'Class VI', 'CLASS VI': 'Class VI',
    'C7': 'Class VII', 'CLASS VII': 'Class VII',
    'C8': 'Class VIII', 'CLASS VIII': 'Class VIII',
    'C9': 'Class IX', 'CLASS IX': 'Class IX',
    'C10': 'Class X', 'CLASS X': 'Class X',
  };

  return shortMap[baseClass] || Object.keys(MARKING_SCHEMES).find(
    k => k.toUpperCase() === baseClass
  ) || 'Class VI';
}

/**
 * Resolve subject display name from subjectId.
 */
function resolveSubjectName(subjectId) {
  const nameMap = {
    'sci': 'Science', 'science': 'Science',
    'math': 'Mathematics', 'mathematics': 'Mathematics',
    'eng': 'English', 'english': 'English',
    'hin': 'Hindi', 'hindi': 'Hindi',
    'sst': 'Social Science', 'social_science': 'Social Science', 'social science': 'Social Science',
  };
  return nameMap[subjectId?.toLowerCase()] || subjectId || 'Subject';
}

export function MarksPage() {
  const { user } = useAuth();
  const { classId, subjectId } = useParams();
  
  // Generate a batchId once per page session
  const [batchId] = useState(() => crypto.randomUUID());
  // Record this batch in history if it gets used (lazy initialization)
  const [historyRecorded, setHistoryRecorded] = useState(false);

  const teacherProfile = useLiveQuery(
    () => (user?.email ? db.teachers.where('email').equals(user.email).first() : null),
    [user?.email]
  );

  const classKey = resolveClassKey(classId);
  const scheme = MARKING_SCHEMES[classKey];
  const terms = scheme?.terms || ['Term 1'];
  const [term, setTerm] = useState(terms[0]);
  const [sectionFilter, setSectionFilter] = useState('All');

  const subjectName = resolveSubjectName(subjectId);
  const className = classKey || classId || 'Class';

  // Get columns from MARKING_SCHEMES for the selected term
  const columns = scheme?.columns?.[term] || [{ id: 'total', label: 'Score', max: 100 }];

  const students = useLiveQuery(
    async () => {
      const all = await db.students.orderBy('rollNo').toArray();
      
      // Determine the section constraint directly from the route classId if present
      const parts = (classId || '').toUpperCase().split('-');
      const routeSection = parts.length > 1 ? parts[1].trim() : '';

      return all.filter(s => {
        const upper = (s.class?.toString() || '').toUpperCase();
        let isClass = true;
        if (classKey === 'Class VI') isClass = ['6','VI','CLASS VI'].includes(upper);
        else if (classKey === 'Class VII') isClass = ['7','VII','CLASS VII'].includes(upper);
        else if (classKey === 'Class VIII') isClass = ['8','VIII','CLASS VIII'].includes(upper);
        else if (classKey === 'Class IX') isClass = ['9','IX','CLASS IX'].includes(upper);
        else if (classKey === 'Class X') isClass = ['10','X','CLASS X'].includes(upper);
        
        if (!isClass) return false;
        
        const sSection = (s.section || '').trim().toUpperCase();
        
        // 1. If teacher used section filter dropdown, apply it strictly
        if (sectionFilter !== 'All') {
          return sSection === sectionFilter.toUpperCase();
        }

        // 2. If the URL contains a section (e.g. Class VIII-A), filter to this section OR no-section
        if (routeSection) {
          if (!sSection) return true; // cross-pollination for students without section
          return sSection === routeSection;
        }
        
        // 3. Otherwise show all sections for this class
        return true;
      });
    },
    [classKey]
  );
  const marks = useLiveQuery(
    () => db.marks.where({ subject: subjectName }).toArray(),
    [subjectName],
  );

  const { triggerSync } = useOfflineSync();

  const handleExportCSV = () => {
    if (!students || !marks) return;

    // Create CSV header
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "Roll No,Student Name," + columns.map(c => c.label).join(",") + ",Total\n";

    // Create rows
    students.forEach(student => {
      let row = `${student.rollNo},${student.name}`;
      let total = 0;

      columns.forEach(col => {
        const mark = marks.find(m => m.studentId === student.id && m.componentId === col.id && m.term === term);
        const score = mark?.score || 0;
        row += `,${score}`;
        total += score;
      });

      row += `,${total}\n`;
      csvContent += row;
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${className}_${subjectName}_${term}_Marks.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmitBatch = async () => {
    if (!navigator.onLine) {
      alert("You are offline. Sync will happen automatically when connected.");
      return;
    }
    await triggerSync();
  };

  if (user?.role !== 'admin' && teacherProfile?.role !== 'admin') {
    const isAssigned = teacherProfile?.subjectsAssigned?.some(
      s => resolveClassKey(s.classId) === classKey && s.subjectId === subjectId
    );
    if (!isAssigned) {
      return (
        <div className="min-h-screen bg-paper flex items-center justify-center p-4">
          <div className="bg-white border border-red-100 rounded-xl p-8 max-w-md w-full text-center shadow-sm">
            <ShieldAlert size={48} className="text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-ink mb-2">Access Denied</h2>
            <p className="text-graphite mb-6">
              You are not assigned to teach {subjectName} for {className}.
            </p>
            <Link to="/teacher/dashboard" className="text-indigo-600 font-medium hover:underline">
              Return to Dashboard
            </Link>
          </div>
        </div>
      );
    }
  }

  return (
    <div className="min-h-screen bg-paper pb-8">
      <div className="max-w-6xl mx-auto px-4 py-4">
        {/* ── Header & Breadcrumbs ── */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Link
              to="/teacher/dashboard"
              className="p-2 rounded-lg bg-white border border-divider hover:bg-gray-50 transition-colors flex items-center justify-center"
            >
              <ArrowLeft size={20} className="text-ink" />
            </Link>
            <div>
              <div className="text-xs text-graphite mb-1 font-medium font-secondary">
                Dashboard &gt; {className} &gt; {subjectName}
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-ink">
                Marks Register
              </h1>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg px-4 py-2 text-sm font-semibold transition-colors"
            >
              <Download size={16} />
              Export CSV
            </button>
            <button
              onClick={handleSubmitBatch}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-4 py-2 text-sm font-semibold transition-colors shadow-sm"
            >
              <UploadCloud size={16} />
              Submit Batch
            </button>
          </div>
        </div>

        {/* ── Selectors ── */}
        <div className="bg-white border border-divider rounded-xl p-4 mb-6 flex flex-wrap gap-4 items-end">
          <div>
            <label htmlFor="term-select" className="block text-sm font-medium text-ink mb-1">
              Exam Term
            </label>
            <select
              id="term-select"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500 outline-none font-medium"
            >
              {terms.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink mb-1">Section</label>
            <select
              value={sectionFilter}
              onChange={(e) => setSectionFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500 outline-none font-medium"
            >
              <option value="All">All Sections</option>
              <option value="A">Section A</option>
              <option value="B">Section B</option>
              <option value="C">Section C</option>
            </select>
          </div>
          <div className="text-sm text-graphite self-center font-secondary">
            <span className="font-medium">{columns.length}</span> assessment columns for <span className="font-medium">{term}</span>
          </div>
        </div>

        {/* ── Table / Grid ── */}
        <div className="bg-white border border-divider rounded-xl shadow-sm overflow-hidden overflow-x-auto">
          <RegisterGrid
            students={students || []}
            subject={subjectName}
            term={term}
            marks={marks || []}
            columns={columns}
            batchId={batchId}
            onMarkSaved={() => {
              if (!historyRecorded) {
                recordHistoryBatch(batchId, `Saved Marks for ${className} ${subjectName} (${term})`);
                setHistoryRecorded(true);
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}
